/**
 * Drill Template Form Screen
 * Create or edit drill timer templates
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Loader2, Plus, X, Grip, Info } from 'lucide-react'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useNavigate, useParams } from 'react-router'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { drillTimerApi } from '@/services/drillTimerService'
import { RESCUE_READY_ACTIONS, ROLE_LABELS } from '@/types/drillTimer'
import { toast } from '@/utils/helpers'

// Draggable Action Component
function DraggableAction({ action, index, onRemove, onReorder }) {
  const [{ opacity }, drop] = useDrop({
    accept: 'sequential-action',
    drop({ draggedIndex }) {
      if (draggedIndex !== index) {
        onReorder(draggedIndex, index)
      }
    },
  })

  const [{ handlerId }, drag] = useDrag(
    () => ({
      type: 'sequential-action',
      item: { draggedIndex: index },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
        isDragging: monitor.isDragging(),
      }),
    }),
    [index],
  )

  return (
    <div
      ref={drop}
      data-handler-id={handlerId}
      className="flex items-center gap-2 rounded bg-gray-50 p-2"
      style={{ opacity }}
    >
      <div ref={drag} className="cursor-move">
        <Grip className="text-muted-foreground h-4 w-4" />
      </div>
      <span className="flex-1">
        {index + 1}. {action.action}
      </span>
      <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(index)}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}

DraggableAction.propTypes = {
  action: PropTypes.shape({
    action: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
}

export function DrillTemplateFormScreen() {
  const navigate = useNavigate()
  const { entityId, templateId } = useParams()
  const queryClient = useQueryClient()
  const isEditMode = !!templateId

  const [formData, setFormData] = useState({
    name: '',
    is_rescue_ready: false,
    is_sequential: false,
    is_non_sequential: false,
    sequential: [],
    non_sequential: [],
    roles: [],
  })

  const [sequentialInput, setSequentialInput] = useState('')
  const [nonSequentialInput, setNonSequentialInput] = useState('')
  const [roleInput, setRoleInput] = useState('')

  // Fetch template data if editing
  const { data: template, isLoading } = useQuery({
    queryKey: ['drillTemplate', entityId, templateId],
    queryFn: () => drillTimerApi.getTemplate(entityId, templateId),
    enabled: isEditMode,
  })

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        is_rescue_ready: template.isRescueReady || template.is_rescue_ready || false,
        is_sequential: template.isSequential || template.is_sequential || false,
        is_non_sequential: template.isNonSequential || template.is_non_sequential || false,
        sequential: template.sequential || [],
        non_sequential: template.nonSequential || template.non_sequential || [],
        roles: template.roles || [],
      })
    }
  }, [template])

  // Auto-populate roles when Rescue Ready is selected
  useEffect(() => {
    if (formData.is_rescue_ready) {
      // Get all default Rescue Ready roles from ROLE_LABELS
      const defaultRoles = Object.values(ROLE_LABELS)

      // Only add roles if they haven't been added already
      const hasDefaultRoles = defaultRoles.every((role) => formData.roles.includes(role))

      if (!hasDefaultRoles && formData.roles.length === 0) {
        setFormData((prev) => ({
          ...prev,
          roles: defaultRoles,
        }))
      }
    }
  }, [formData.is_rescue_ready, formData.roles])

  const createMutation = useMutation({
    mutationFn: (data) => drillTimerApi.createTemplate(entityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['drillTemplates', entityId])
      toast('Template created successfully', 'success')
      navigate(`/accounts/${entityId}/drill-timer/templates`)
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to create template'
      toast(errorMessage, 'error')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => drillTimerApi.updateTemplate(entityId, templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['drillTemplates', entityId])
      queryClient.invalidateQueries(['drillTemplate', entityId, templateId])
      toast('Template updated successfully', 'success')
      navigate(`/accounts/${entityId}/drill-timer/templates`)
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update template'
      toast(errorMessage, 'error')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validation
    if (!formData.name.trim()) {
      toast('Template name is required', 'error')
      return
    }

    if (!formData.is_rescue_ready && !formData.is_sequential && !formData.is_non_sequential) {
      toast('Please select at least one template type', 'error')
      return
    }

    if (formData.is_sequential && formData.sequential.length === 0) {
      toast('Please add at least one sequential action', 'error')
      return
    }

    if (formData.is_non_sequential && formData.non_sequential.length === 0) {
      toast('Please add at least one non-sequential action', 'error')
      return
    }

    if (isEditMode) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const addSequentialAction = () => {
    if (sequentialInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        sequential: [...prev.sequential, { action: sequentialInput.trim() }],
      }))
      setSequentialInput('')
    }
  }

  const removeSequentialAction = (index) => {
    setFormData((prev) => ({
      ...prev,
      sequential: prev.sequential.filter((_, i) => i !== index),
    }))
  }

  const addNonSequentialAction = () => {
    if (nonSequentialInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        non_sequential: [...prev.non_sequential, { action: nonSequentialInput.trim() }],
      }))
      setNonSequentialInput('')
    }
  }

  const removeNonSequentialAction = (index) => {
    setFormData((prev) => ({
      ...prev,
      non_sequential: prev.non_sequential.filter((_, i) => i !== index),
    }))
  }

  const addRole = () => {
    if (roleInput.trim() && !formData.roles.includes(roleInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        roles: [...prev.roles, roleInput.trim()],
      }))
      setRoleInput('')
    }
  }

  const removeRole = (role) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.filter((r) => r !== role),
    }))
  }

  const reorderSequentialAction = (dragIndex, dropIndex) => {
    setFormData((prev) => {
      const updatedSequential = [...prev.sequential]
      const [draggedItem] = updatedSequential.splice(dragIndex, 1)
      updatedSequential.splice(dropIndex, 0, draggedItem)
      return {
        ...prev,
        sequential: updatedSequential,
      }
    })
  }

  if (isEditMode && isLoading) {
    return (
      <Layout
        breadcrumbs={[
          { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
          { pathname: `/accounts/${entityId}/drill-timer/templates`, title: 'Templates' },
          'Edit',
        ]}
      >
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout
      breadcrumbs={[
        { pathname: `/accounts/${entityId}/drill-timer`, title: 'Drill Timer' },
        { pathname: `/accounts/${entityId}/drill-timer/templates`, title: 'Templates' },
        isEditMode ? 'Edit' : 'New',
      ]}
    >
      <Layout.Heading title={isEditMode ? 'Edit Template' : 'New Template'} />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Deep Water Rescue"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Template Types */}
        <Card>
          <CardHeader>
            <CardTitle>Template Type *</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="rescue-ready"
                checked={formData.is_rescue_ready}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_rescue_ready: checked })
                }
              />
              <Label htmlFor="rescue-ready" className="cursor-pointer">
                Rescue Ready (Pre-defined emergency protocol)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sequential"
                checked={formData.is_sequential}
                onCheckedChange={(checked) => setFormData({ ...formData, is_sequential: checked })}
              />
              <Label htmlFor="sequential" className="cursor-pointer">
                Sequential (Actions must be completed in order)
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="non-sequential"
                checked={formData.is_non_sequential}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_non_sequential: checked })
                }
              />
              <Label htmlFor="non-sequential" className="cursor-pointer">
                Non-Sequential (Actions can be completed in any order)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Rescue Ready Pre-defined Actions */}
        {formData.is_rescue_ready && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Rescue Ready Protocol Actions
                <Badge variant="secondary" className="text-xs">
                  <Info className="mr-1 h-3 w-3" />
                  Pre-defined
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground mb-4">
                  The Rescue Ready template uses a standardized emergency response protocol with
                  the following actions:
                </p>
                <div className="space-y-2">
                  {RESCUE_READY_ACTIONS.map((action, index) => (
                    <div
                      key={action.definition}
                      className="flex items-start gap-3 rounded border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="bg-primary/10 text-primary flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{action.name}</div>
                        {action.hint && (
                          <div className="text-muted-foreground mt-0.5 text-xs">{action.hint}</div>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {action.states.map((state) => (
                            <Badge key={state} variant="outline" className="text-xs">
                              {state}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sequential Actions */}
        {formData.is_sequential && (
          <Card>
            <CardHeader>
              <CardTitle>Sequential Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={sequentialInput}
                  onChange={(e) => setSequentialInput(e.target.value)}
                  placeholder="Enter action name"
                  onKeyPress={(e) =>
                    e.key === 'Enter' && (e.preventDefault(), addSequentialAction())
                  }
                />
                <Button type="button" onClick={addSequentialAction}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {formData.sequential.length > 0 && (
                <div className="space-y-2">
                  <div className="text-muted-foreground mb-2 flex items-center gap-1 text-xs">
                    <Grip className="h-3 w-3" />
                    Drag to reorder
                  </div>
                  {formData.sequential.map((item, index) => (
                    <DraggableAction
                      key={item.action || index}
                      action={item}
                      index={index}
                      onRemove={removeSequentialAction}
                      onReorder={reorderSequentialAction}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Non-Sequential Actions */}
        {formData.is_non_sequential && (
          <Card>
            <CardHeader>
              <CardTitle>Non-Sequential Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={nonSequentialInput}
                  onChange={(e) => setNonSequentialInput(e.target.value)}
                  placeholder="Enter action name"
                  onKeyPress={(e) =>
                    e.key === 'Enter' && (e.preventDefault(), addNonSequentialAction())
                  }
                />
                <Button type="button" onClick={addNonSequentialAction}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {formData.non_sequential.length > 0 && (
                <div className="space-y-2">
                  {formData.non_sequential.map((item, index) => (
                    <div
                      key={item.action || index}
                      className="flex items-center gap-2 rounded bg-gray-50 p-2"
                    >
                      <span className="flex-1">{item.action}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeNonSequentialAction(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Roles */}
        <Card>
          <CardHeader>
            <CardTitle>Available Roles (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.is_rescue_ready && formData.roles.length > 0 && (
              <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                <p className="text-sm text-blue-900">
                  The following roles are added by default because you selected the Rescue Ready
                  drill timer: AED Operator, Assessment, Compressions, DPIC, Extrication, In Water
                  Rescuer, O2 Provider and Wetsuit Removal
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={roleInput}
                onChange={(e) => setRoleInput(e.target.value)}
                placeholder="e.g., Lifeguard, First Responder"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRole())}
              />
              <Button type="button" onClick={addRole}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {formData.roles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.roles.map((role) => (
                  <div
                    key={role}
                    className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1"
                  >
                    <span className="text-sm">{role}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRole(role)}
                      className="h-auto p-0 hover:bg-transparent"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/accounts/${entityId}/drill-timer/templates`)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="flex-1"
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditMode ? 'Update Template' : 'Create Template'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Layout>
  )
}
